// ===================================================================
// KAIXO - Admin Verifications Section
// ===================================================================
// Cola de verificaciones pendientes + vista de revisión.
// ===================================================================

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    adminVerificationsList,
    adminVerificationsGet,
    adminVerificationApprove,
    adminVerificationReject,
    adminVerificationRequestInfo,
    areaHeaders
} from '../../../services/adminApi';
import api from '../../../services/api';
import './AdminVerificationsSection.css';

const STATUS_LABELS = {
    pending: 'Pendientes',
    info_requested: 'Info pedida',
    approved: 'Aprobadas',
    rejected: 'Rechazadas',
    expired: 'Expiradas',
    remove: 'Eliminar'
};

const LEVEL_LABELS = {
    basic: { label: 'Identidad', icon: '🟢' },
    creator: { label: 'Creador', icon: '🟣' },
    business: { label: 'Negocio', icon: '🔵' }
};

function timeAgo(date) {
    if (!date) return '';
    const sec = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (sec < 60) return `${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
}

export default function AdminVerificationsSection() {
    const [statusFilter, setStatusFilter] = useState('pending');
    const [list, setList] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [selected, setSelected] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [error, setError] = useState('');

    const reload = useCallback(async () => {
        setLoadingList(true);
        try {
            const { data } = await adminVerificationsList({ status: statusFilter, limit: 100 });
            setList(data?.verifications || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoadingList(false);
        }
    }, [statusFilter]);

    useEffect(() => { reload(); }, [reload]);

    const openDetail = async (id) => {
        setLoadingDetail(true);
        try {
            const { data } = await adminVerificationsGet(id);
            setSelected(data?.verification || null);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleApprove = async () => {
        if (!selected) return;
        const notes = prompt('Notas internas (opcional):') || '';
        try {
            await adminVerificationApprove(selected._id, notes);
            setSelected(null);
            reload();
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    const handleReject = async () => {
        if (!selected) return;
        const reason = prompt('Motivo del rechazo (visible al usuario):');
        if (!reason || reason.length < 5) return;
        const notes = prompt('Notas internas (opcional):') || '';
        try {
            await adminVerificationReject(selected._id, reason, notes);
            setSelected(null);
            reload();
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    const handleRequestInfo = async () => {
        if (!selected) return;
        const message = prompt('¿Qué información adicional necesitas del usuario?');
        if (!message) return;
        try {
            await adminVerificationRequestInfo(selected._id, message);
            setSelected(null);
            reload();
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    // Borra el registro histórico — útil para limpiar duplicados de la
    // lista de aprobadas. NO toca la insignia (canal/usuario), solo el
    // registro. Para quitar la insignia usar la pestaña "Eliminar".
    const handleDeleteRecord = async () => {
        if (!selected) return;
        if (!window.confirm(
            'Vas a borrar este registro del historial. Esta acción NO retira la insignia ' +
            'del canal o usuario — solo limpia la entrada de la lista. Para retirar la ' +
            'insignia usa la pestaña "Eliminar".\n\n¿Continuar?'
        )) return;
        try {
            await api.delete(`/admin/verifications/${selected._id}`, {
                headers: areaHeaders('verification')
            });
            setSelected(null);
            reload();
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    return (
        <div className="adv-wrap">
            {/* Filtros de estado */}
            <div className="adv-filters">
                {Object.entries(STATUS_LABELS).map(([k, label]) => (
                    <button
                        key={k}
                        className={`adv-filter ${statusFilter === k ? 'is-active' : ''} ${k === 'remove' ? 'adv-filter-danger' : ''}`}
                        onClick={() => { setStatusFilter(k); setSelected(null); }}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {error && <div className="ap-error">{error}</div>}

            {/* Pestaña Eliminar: vista distinta — buscador + checkboxes */}
            {statusFilter === 'remove' && <RemoveVerificationsView />}

            {statusFilter !== 'remove' && (
            <div className="adv-split">
                {/* Lista */}
                <div className="adv-list-col">
                    {loadingList && <div className="ap-loading">Cargando…</div>}
                    {!loadingList && list.length === 0 && (
                        <div className="adv-empty">No hay verificaciones en estado "{STATUS_LABELS[statusFilter]}".</div>
                    )}
                    {list.map(v => {
                        const lvl = LEVEL_LABELS[v.level] || { label: v.level, icon: '?' };
                        return (
                            <button
                                key={v._id}
                                className={`adv-item ${selected?._id === v._id ? 'is-selected' : ''}`}
                                onClick={() => openDetail(v._id)}
                            >
                                <div className="adv-item-head">
                                    <strong>
                                        {v.channel?.name?.trim()
                                            ? <>📡 {v.channel.name}</>
                                            : (v.user?.name || '—')}
                                    </strong>
                                    <span className="adv-item-time">{timeAgo(v.submittedAt)}</span>
                                </div>
                                <div className="adv-item-meta">
                                    <span className="adv-level">{lvl.icon} {lvl.label}</span>
                                    {v.channel?.name?.trim() && v.user?.name && (
                                        <span> · 👤 {v.user.name}</span>
                                    )}
                                    {v.declared?.country && <span> · {v.declared.country}</span>}
                                    {v.declared?.companyName && <span> · {v.declared.companyName}</span>}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Detalle */}
                <div className="adv-detail-col">
                    {!selected && !loadingDetail && (
                        <div className="adv-detail-empty">Selecciona una solicitud de la lista.</div>
                    )}
                    {loadingDetail && <div className="ap-loading">Cargando…</div>}
                    {selected && !loadingDetail && (
                        <DetailView
                            v={selected}
                            onApprove={handleApprove}
                            onReject={handleReject}
                            onRequestInfo={handleRequestInfo}
                            onDeleteRecord={handleDeleteRecord}
                            onClose={() => setSelected(null)}
                        />
                    )}
                </div>
            </div>
            )}
        </div>
    );
}

function DocImg({ url, label }) {
    if (!url) return (
        <div className="adv-doc adv-doc-empty">
            <div className="adv-doc-label">{label}</div>
            <div className="adv-doc-empty-text">— sin documento —</div>
        </div>
    );
    return (
        <div className="adv-doc">
            <div className="adv-doc-label">{label}</div>
            <a href={url} target="_blank" rel="noreferrer">
                <img src={url} alt={label} />
            </a>
        </div>
    );
}

function DetailView({ v, onApprove, onReject, onRequestInfo, onDeleteRecord, onClose }) {
    const lvl = LEVEL_LABELS[v.level] || { label: v.level, icon: '?' };
    const docs = v.documents || {};
    const declared = v.declared || {};
    const isFinal = ['approved', 'rejected', 'expired'].includes(v.status);

    return (
        <div className="adv-detail">
            <div className="adv-detail-head">
                <h3>
                    {v.user?.name} · {lvl.icon} {lvl.label}
                    {v.channel?.name && (
                        <span style={{ fontSize: 13, color: '#666', marginLeft: 8 }}>
                            (canal: <strong>{v.channel.name}</strong>)
                        </span>
                    )}
                </h3>
                <button className="adv-close" onClick={onClose}>✕</button>
            </div>

            <div className="adv-section">
                <h4>Datos declarados</h4>
                <table className="adv-table">
                    <tbody>
                        {declared.legalName && <tr><th>Nombre legal</th><td>{declared.legalName}</td></tr>}
                        {declared.documentNumber && <tr><th>Número de documento</th><td>{declared.documentNumber}</td></tr>}
                        {declared.country && <tr><th>País</th><td>{declared.country}</td></tr>}
                        {declared.companyName && <tr><th>Empresa</th><td>{declared.companyName}</td></tr>}
                        {declared.taxId && <tr><th>Tax ID</th><td>{declared.taxId}</td></tr>}
                        {declared.registryDate && (
                            <tr><th>Fecha del extracto registral</th><td>{new Date(declared.registryDate).toLocaleDateString()}</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {(docs.idFront || docs.idBack || docs.selfie) && (
                <div className="adv-section">
                    <h4>Identidad personal</h4>
                    <div className="adv-docs-grid">
                        <DocImg url={docs.idFront} label="DNI/Pasaporte (anverso)" />
                        <DocImg url={docs.idBack} label="Reverso" />
                        <DocImg url={docs.selfie} label="Selfie con documento" />
                    </div>
                </div>
            )}

            {(docs.articles || docs.registryExtract || docs.powerOfAttorney) && (
                <div className="adv-section">
                    <h4>Documentos de la empresa</h4>
                    <div className="adv-docs-grid">
                        <DocImg url={docs.articles} label="Acta constitutiva" />
                        <DocImg url={docs.registryExtract} label="Certificado registro mercantil" />
                        <DocImg url={docs.powerOfAttorney} label="Poderes del representante" />
                    </div>
                </div>
            )}

            {docs.socialLinks?.length > 0 && (
                <div className="adv-section">
                    <h4>Enlaces a redes</h4>
                    <ul className="adv-links">
                        {docs.socialLinks.map((l, i) => (
                            <li key={i}><a href={l} target="_blank" rel="noreferrer">{l}</a></li>
                        ))}
                    </ul>
                </div>
            )}

            {v.adminNotes && (
                <div className="adv-section">
                    <h4>Notas internas</h4>
                    <p>{v.adminNotes}</p>
                </div>
            )}

            {v.rejectionReason && (
                <div className="adv-section adv-section-warn">
                    <h4>Motivo rechazo</h4>
                    <p>{v.rejectionReason}</p>
                </div>
            )}

            {v.infoRequested && (
                <div className="adv-section adv-section-info">
                    <h4>Información solicitada al usuario</h4>
                    <p>{v.infoRequested}</p>
                </div>
            )}

            {!isFinal && (
                <div className="adv-actions">
                    <button className="adv-btn adv-btn-approve" onClick={onApprove}>✓ Aprobar</button>
                    <button className="adv-btn adv-btn-reject" onClick={onReject}>✗ Rechazar</button>
                    <button className="adv-btn adv-btn-info" onClick={onRequestInfo}>↩ Pedir más info</button>
                </div>
            )}

            {isFinal && (
                <div className="adv-actions" style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 14 }}>
                    <p style={{ fontSize: 12, color: '#666', margin: '0 0 8px 0' }}>
                        ⚠ Limpiar registro histórico — útil para borrar duplicados o
                        solicitudes obsoletas. <strong>NO retira la insignia</strong> del
                        canal o usuario, solo elimina la entrada de esta lista.
                    </p>
                    <button
                        className="adv-btn"
                        style={{ background: '#7f1d1d', color: '#fff' }}
                        onClick={onDeleteRecord}
                    >
                        🗑 Eliminar este registro
                    </button>
                </div>
            )}
        </div>
    );
}

// ─── Pestaña "Eliminar verificaciones" ───────────────────────────
// Lista usuarios verificados con buscador + checkbox + acción bulk.
// Al confirmar:
//   1. Llama POST /admin/users/:id/remove-verification con motivo
//   2. Backend manda mensaje automático via Support explicando la baja
// Motivos estándar para eliminar una verificación. El admin puede elegir
// uno o "otro" para escribirlo a mano. El texto se envía al usuario por
// mensaje de KAIXO Support.
const STANDARD_REMOVE_REASONS = [
    {
        id: 'fake_info',
        label: 'Información falsa o engañosa',
        text: 'Hemos detectado información falsa o engañosa en tu cuenta. La verificación implica autenticidad — al detectar contenido no veraz hemos retirado la insignia.'
    },
    {
        id: 'community_violations',
        label: 'Incumplimiento normas comunitarias',
        text: 'Tras revisar tu actividad reciente, hemos detectado incumplimientos repetidos de las normas comunitarias. La verificación exige un comportamiento ejemplar y por eso retiramos la insignia.'
    },
    {
        id: 'inactive',
        label: 'Inactividad / actividad insuficiente',
        text: 'Tu cuenta no cumple ya con la actividad mínima exigida para mantener la verificación. Recuperarás la insignia en cuanto vuelvas a publicar con regularidad y solicites de nuevo la verificación.'
    },
    {
        id: 'identity_doubt',
        label: 'Dudas sobre la identidad',
        text: 'Hemos recibido información que pone en duda la autenticidad de la identidad asociada a tu cuenta verificada. Puedes solicitar de nuevo la verificación aportando documentación actualizada.'
    },
    {
        id: 'other',
        label: 'Otro (escribir motivo)',
        text: ''
    }
];

function RemoveVerificationsView() {
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);   // canales + users legacy
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(new Set()); // sets de "key" (channel-id o user-id)
    const [reasonId, setReasonId] = useState(STANDARD_REMOVE_REASONS[0].id);
    const [customReason, setCustomReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');

    // Texto efectivo que se manda al backend (estándar o custom)
    const reason = useMemo(() => {
        const std = STANDARD_REMOVE_REASONS.find(r => r.id === reasonId);
        if (!std) return '';
        if (std.id === 'other') return customReason.trim();
        return std.text;
    }, [reasonId, customReason]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            // Endpoint específico que devuelve cada CANAL verificado +
            // usuarios legacy con verificación a nivel perfil. Es lo que
            // realmente queremos eliminar (la insignia vive en el canal).
            const { data } = await api.get('/admin/verified-entities');
            setItems(data?.items || []);
        } catch (e) {
            console.warn('load verified entities:', e?.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const filtered = useMemo(() => {
        if (!search.trim()) return items;
        const q = search.trim().toLowerCase();
        return items.filter(it =>
            (it.userName || '').toLowerCase().includes(q)
            || (it.userEmail || '').toLowerCase().includes(q)
            || (it.userPhone || '').includes(q)
            || (it.channelName || '').toLowerCase().includes(q)
        );
    }, [items, search]);

    const toggle = (key) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleAll = () => {
        if (selected.size === filtered.length) setSelected(new Set());
        else setSelected(new Set(filtered.map(it => it.key)));
    };

    const handleBulkRemove = async () => {
        if (selected.size === 0) return;
        if (!reason.trim()) {
            setMsg(reasonId === 'other'
                ? '⚠ Debes escribir el motivo. Se enviará al usuario por Support.'
                : '⚠ Elige un motivo. Se enviará al usuario por Support.');
            return;
        }
        if (!window.confirm(
            `¿Quitar verificación a ${selected.size} ${selected.size === 1 ? 'entidad' : 'entidades'}?\n\n` +
            `Motivo: ${reason}\n\nSe enviará automáticamente un mensaje desde KAIXO Support al titular de cada una.`
        )) return;

        setBusy(true);
        setMsg('');
        let ok = 0, fail = 0;
        for (const it of items.filter(i => selected.has(i.key))) {
            try {
                // remove-verification acepta channelId opcional. Si va, quita
                // la verificación del canal; si no, del user (legacy).
                await api.post(`/admin/users/${it.userId}/remove-verification`, {
                    reason,
                    channelId: it.channelId || undefined,
                    notify: true
                });
                ok++;
            } catch {
                fail++;
            }
        }
        setMsg(`✓ ${ok} verificación(es) eliminada(s)${fail > 0 ? `, ${fail} fallaron` : ''}. Mensajes de Support enviados.`);
        setSelected(new Set());
        setCustomReason('');
        setReasonId(STANDARD_REMOVE_REASONS[0].id);
        await reload();
        setBusy(false);
    };

    return (
        <div className="adv-remove-wrap">
            <div className="adv-remove-help">
                <strong>⚠ Eliminar verificaciones</strong> — busca al usuario, marca la(s) verificación(es)
                a quitar, escribe el motivo y confirma. Se enviará un mensaje automático desde
                KAIXO Support a cada usuario explicando la decisión.
            </div>

            <input
                type="text"
                className="adv-remove-search"
                placeholder="Buscar por nombre, email o teléfono..."
                value={search}
                onChange={e => setSearch(e.target.value)}
            />

            {loading && <div className="ap-loading">Cargando usuarios verificados…</div>}

            {!loading && filtered.length === 0 && (
                <div className="adv-empty">No hay usuarios verificados que coincidan.</div>
            )}

            {!loading && filtered.length > 0 && (
                <>
                    <div className="adv-remove-actions-top">
                        <label className="adv-remove-toggle-all">
                            <input
                                type="checkbox"
                                checked={selected.size > 0 && selected.size === filtered.length}
                                onChange={toggleAll}
                            />
                            {selected.size === filtered.length ? 'Desmarcar todos' : `Marcar todos (${filtered.length})`}
                        </label>
                        <span className="adv-remove-count">
                            {selected.size} seleccionado(s)
                        </span>
                    </div>

                    <div className="adv-remove-list">
                        {filtered.map(it => {
                            const checked = selected.has(it.key);
                            const lvl = LEVEL_LABELS[it.verifiedLevel] || { label: it.verifiedLevel, icon: '?' };
                            const avatar = it.channelAvatar || it.userAvatar;
                            const fallbackChar = (it.channelName || it.userName || '?')[0].toUpperCase();
                            return (
                                <label key={it.key} className={`adv-remove-row ${checked ? 'is-checked' : ''}`}>
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggle(it.key)}
                                    />
                                    <div className="adv-remove-row-avatar">
                                        {avatar
                                            ? <img src={avatar} alt="" />
                                            : <span>{fallbackChar}</span>}
                                    </div>
                                    <div className="adv-remove-row-info">
                                        <div className="adv-remove-row-name">
                                            {it.kind === 'channel' ? (
                                                <>
                                                    {it.channelName}
                                                    <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>
                                                        ({it.channelType})
                                                    </span>
                                                </>
                                            ) : (
                                                <>{it.userName} <span style={{ fontSize: 11, color: '#999' }}>· perfil legacy</span></>
                                            )}
                                            <span className="adv-remove-row-level">{lvl.icon} {lvl.label}</span>
                                        </div>
                                        <div className="adv-remove-row-meta">
                                            {it.kind === 'channel' && <>👤 {it.userName} · </>}
                                            {it.userPhone || '—'}{it.userEmail ? ` · ${it.userEmail}` : ''}
                                        </div>
                                    </div>
                                </label>
                            );
                        })}
                    </div>

                    <div className="adv-remove-foot">
                        <div className="adv-remove-reasons" style={{ display: 'grid', gap: 6, marginBottom: 8 }}>
                            <strong style={{ fontSize: 13 }}>Motivo del aviso al usuario:</strong>
                            {STANDARD_REMOVE_REASONS.map(r => (
                                <label key={r.id} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 8,
                                    fontSize: 13, padding: '6px 8px', borderRadius: 6,
                                    background: reasonId === r.id ? '#fff5f5' : 'transparent',
                                    border: reasonId === r.id ? '1px solid #fecaca' : '1px solid transparent',
                                    cursor: busy ? 'not-allowed' : 'pointer'
                                }}>
                                    <input
                                        type="radio"
                                        name="remove-reason"
                                        value={r.id}
                                        checked={reasonId === r.id}
                                        onChange={() => setReasonId(r.id)}
                                        disabled={busy}
                                        style={{ marginTop: 3 }}
                                    />
                                    <span>
                                        <strong>{r.label}</strong>
                                        {r.text && (
                                            <div style={{ fontSize: 12, color: '#666', marginTop: 2, lineHeight: 1.4 }}>
                                                {r.text}
                                            </div>
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                        {reasonId === 'other' && (
                            <textarea
                                className="adv-remove-reason"
                                value={customReason}
                                onChange={e => setCustomReason(e.target.value)}
                                placeholder="Escribe el motivo personalizado que se enviará al usuario por mensaje de Support…"
                                maxLength={1000}
                                rows={3}
                                disabled={busy}
                            />
                        )}
                        {msg && <div className="adv-remove-msg">{msg}</div>}
                        <button
                            className="adv-remove-btn"
                            onClick={handleBulkRemove}
                            disabled={busy || selected.size === 0}
                        >
                            {busy ? 'Eliminando…' : `Eliminar ${selected.size} verificación(es)`}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
